import React, { useEffect, useState } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Download, Filter, Search, Edit, Trash2 } from 'lucide-react';
import { exportToExcel, formatExpensesForExport } from '../utils/excelExport';

const expenseCategories = [
  'Decoration Items',
  'House Keeping Items',
  'Food and Beverages',
  'Stationaries',
  'Electricity',
  'Maintenance',
  'Other'
];

export default function BranchExpenses() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    branch: 'all',
    search: '',
    start_date: '',
    end_date: ''
  });
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    description: '',
    payment_mode: 'cash',
    date: new Date().toISOString().split('T')[0],
    branch_id: user?.branch_id || '',
    user_id: ''
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchBranches();
      fetchUsers();
    }
  }, [user]);

  useEffect(() => {
    // Filter users based on selected branch
    if (formData.branch_id && users.length > 0) {
      const branchUsers = users.filter(u => u.branch_id === formData.branch_id);
      setFilteredUsers(branchUsers);
    } else {
      setFilteredUsers([]);
    }
  }, [formData.branch_id, users]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      console.log('Fetched branches:', response.data);
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
      toast.error('Failed to fetch branches');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await api.get('/branch-expenses');
      setExpenses(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        category: formData.category,
        amount: parseFloat(formData.amount),
        description: formData.description,
        payment_mode: formData.payment_mode,
        date: formData.date
      };
      
      // Include branch_id only for admin
      if (user?.role === 'admin') {
        payload.branch_id = formData.branch_id;
      }
      
      if (editingExpense) {
        // Update existing expense
        await api.put(`/branch-expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated successfully');
      } else {
        // Create new expense
        await api.post('/branch-expenses', payload);
        toast.success('Expense added successfully');
      }
      
      setDialogOpen(false);
      setEditingExpense(null);
      setFormData({
        category: '',
        amount: '',
        description: '',
        payment_mode: 'cash',
        date: new Date().toISOString().split('T')[0],
        branch_id: user?.branch_id || '',
        user_id: ''
      });
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingExpense ? 'update' : 'add'} expense`);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      amount: expense.amount,
      description: expense.description,
      payment_mode: expense.payment_mode,
      date: expense.date,
      branch_id: expense.branch_id || user?.branch_id || '',
      user_id: expense.user_id || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/branch-expenses/${expenseToDelete.id}`);
      toast.success('Expense deleted successfully');
      setDeleteDialogOpen(false);
      setExpenseToDelete(null);
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleExport = () => {
    try {
      const formattedData = formatExpensesForExport(filteredExpenses);
      exportToExcel(formattedData, 'branch_expenses', 'Branch Expenses');
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!expense.description.toLowerCase().includes(search) && 
          !expense.category.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (filters.category && filters.category !== 'all') {
      if (expense.category !== filters.category) return false;
    }
    if (filters.branch && filters.branch !== 'all') {
      if (expense.branch_id !== filters.branch) return false;
    }
    if (filters.start_date && expense.date < filters.start_date) return false;
    if (filters.end_date && expense.date > filters.end_date) return false;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }} data-testid="branch-expenses-title">
            Branch Expenses
          </h1>
          <p className="text-slate-600 mt-1">Manage your branch expenses</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExport} variant="outline" className="gap-2" disabled={filteredExpenses.length === 0}>
            <Download size={16} />
            Export to Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingExpense(null);
              setFormData({
                category: '',
                amount: '',
                description: '',
                payment_mode: 'cash',
                date: new Date().toISOString().split('T')[0],
                branch_id: user?.branch_id || '',
                user_id: ''
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-expense-btn" className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white gap-2">
                <Plus size={16} />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {user?.role === 'admin' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="branch">Branch *</Label>
                      <Select value={formData.branch_id} onValueChange={(value) => setFormData({ ...formData, branch_id: value, user_id: '' })} required>
                        <SelectTrigger>
                          <SelectValue placeholder={branches.length > 0 ? "Select branch" : "Loading branches..."} />
                        </SelectTrigger>
                        <SelectContent>
                          {branches.length === 0 ? (
                            <div className="p-2 text-sm text-slate-500">No branches available</div>
                          ) : (
                            branches.map(branch => (
                              <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {branches.length === 0 && <p className="text-xs text-red-500">Branches not loaded. Please refresh.</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="user">Created By (Optional)</Label>
                      <Select value={formData.user_id || undefined} onValueChange={(value) => setFormData({ ...formData, user_id: value })} disabled={!formData.branch_id}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select user (optional) - defaults to admin" />
                        </SelectTrigger>
                        <SelectContent>
                          {filteredUsers.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.username} ({u.role})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">Leave empty to use your admin account</p>
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })} required>
                    <SelectTrigger data-testid="expense-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Amount *</Label>
                  <Input
                    id="amount"
                    data-testid="expense-amount-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    data-testid="expense-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    data-testid="expense-date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Payment Mode *</Label>
                  <RadioGroup value={formData.payment_mode} onValueChange={(value) => setFormData({ ...formData, payment_mode: value })}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="cash" id="exp_cash" data-testid="expense-payment-cash" />
                      <Label htmlFor="exp_cash" className="cursor-pointer">Cash</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="online" id="exp_online" data-testid="expense-payment-online" />
                      <Label htmlFor="exp_online" className="cursor-pointer">Online</Label>
                    </div>
                  </RadioGroup>
                </div>

                <Button data-testid="submit-expense-btn" type="submit" className="w-full">
                  {editingExpense ? 'Update Expense' : 'Add Expense'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter size={20} />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  placeholder="Search description..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10"
                />
              </div>
            </div>

            {user?.role === 'admin' && (
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={filters.branch} onValueChange={(value) => setFilters({ ...filters, branch: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={filters.category} onValueChange={(value) => setFilters({ ...filters, category: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {expenseCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Total Expenses: ₹{filteredExpenses.reduce((sum, e) => sum + e.amount, 0).toFixed(2)}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-slate-600">Loading expenses...</p>
            </div>
          ) : filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No expenses found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredExpenses.map((expense) => (
                <div
                  key={expense.id}
                  data-testid={`expense-${expense.id}`}
                  className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{expense.category}</h3>
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 capitalize">
                          {expense.payment_mode}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{expense.description}</p>
                      <div className="flex gap-4 mt-2 text-xs text-slate-500">
                        <span>Date: {expense.date}</span>
                        <span>By: {expense.created_by_username}</span>
                      </div>
                    </div>
                    <div className="text-right flex items-start gap-2">
                      <div>
                        <p className="text-lg font-bold text-slate-900">₹{expense.amount.toFixed(2)}</p>
                      </div>
                      {user?.role === 'admin' && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(expense)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setExpenseToDelete(expense);
                              setDeleteDialogOpen(true);
                            }}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Expense</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Are you sure you want to delete this expense? This action cannot be undone.
          </p>
          {expenseToDelete && (
            <div className="p-3 bg-slate-50 rounded-lg mt-2">
              <p className="text-sm"><strong>Category:</strong> {expenseToDelete.category}</p>
              <p className="text-sm"><strong>Amount:</strong> ₹{expenseToDelete.amount.toFixed(2)}</p>
              <p className="text-sm"><strong>Description:</strong> {expenseToDelete.description}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
